// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { removeNullAndUndefinedProps } from "@transformers/object";
import { parseSchema } from "@utils/schema";
import Ajv from "ajv";

import serviceActionCreators from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { HierarchyAssignment } from "@modules/service/types";
import type {
  WellbeingService,
  WellbeingServiceContent,
} from "@modules/service/reducer/wellbeing-services/types";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  LinearWizard,
  Step,
} from "@components";
import Step1MainDetails from "./main-details";

type PropsFromState = $Exact<{
  draft: ?WellbeingServiceContent,
  container: ?WellbeingService,
  schema: ?Object,
  assignments: Array<HierarchyAssignment>,
}>;

type PropsFromDispatch = $Exact<{
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
  setShowEditModal: typeof serviceActionCreators.SetShowEditWellbeingServiceModal,
  editWellbeingService: typeof serviceActionCreators.EditWellbeingService,
  setSchema: typeof serviceActionCreators.SetWellbeingServiceSchema,
  updateAssignments: typeof serviceActionCreators.EditWellbeingServiceAssignments,
  setAssignments: typeof serviceActionCreators.SetWellbeingServiceAssignments,
  setWellbeingServiceDraft: typeof serviceActionCreators.SetWellbeingServiceDraft,
  setWellbeingServiceCurrentDetails: typeof serviceActionCreators.SetWellbeingServiceCurrentDetails,
}>;

type OwnProps = $Exact<{
  clientId: number,
  defaultStep: number,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class EditWellbeingServicesModal extends Component<Props> {
  validate: *;

  componentDidMount() {
    this.loadSchema();

    this.props.getHierarchyTree({
      clientId: this.props.clientId,
    });

    if (!this.validate && this.props.schema) {
      this.makeValidator();
    }
  }

  makeValidator() {
    this.validate = new Ajv().compile(this.props.schema);
  }

  loadSchema = async () => {
    const schema = await parseSchema("WellbeingServicesDTO", {
      clientId: this.props.clientId,
    });

    this.props.setSchema(schema);
  };

  handleCancel = () => {
    this.props.setShowEditModal({ show: false });
    this.props.setWellbeingServiceDraft(null);
    this.props.setWellbeingServiceCurrentDetails();
  };

  handleSubmit = () => {
    if (this.props.container && this.props.draft) {
      let draft = this.props.draft;
      const newIsActive = draft?.isActive; // new value
      const isActive = this.props.container?.content.isActive; // prev value

      if (isActive !== newIsActive) {
        draft = {
          ...draft,
          activeFrom: draft.activeTo ? new Date().toISOString() : draft.activeFrom,
          activeTo: draft.activeTo ? null : new Date().toISOString(),
        };
      }

      this.props.editWellbeingService({
        clientId: this.props.clientId,
        providerId: this.props.container?.id,
        draft: draft,
      });
    }

    if (this.props.container && this.props.container.hierarchyAssignments) {
      this.props.updateAssignments({
        container: this.props.container,
        assignments: this.props.assignments,
      });
    }
  };

  get isValid() {
    return this.validateDraft;
  }

  get validateDraft() {
    if (!this.validate) {
      return false;
    }
    return this.validate(removeNullAndUndefinedProps(this.props.draft || {}));
  }

  get draft() {
    return this.props.draft || this.props.container?.content || {};
  }

  render() {
    if (!this.validate && this.props.schema) {
      this.makeValidator();
    }

    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("EDIT_FOR_NAME", {
            name: this.props.container?.id,
          })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          <LinearWizard defaultStep={this.props.defaultStep} data={[]}>
            <Step
              component={Step1MainDetails}
              title={this.props.t("EDIT_EAP_MODAL_STEP_1_TITLE")}
            />
          </LinearWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button materialIcon="save" onClick={this.handleSubmit} disabled={!this.isValid}>
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    container: serviceSelectors.getCurrentWellbeingServiceDetails(state),
    draft: serviceSelectors.getWellbeingServicesDraft(state),
    schema: serviceSelectors.getWellbeingServiceSchema(state),
    assignments: serviceSelectors.getWellbeingServiceAssignments(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
  setShowEditModal: serviceActionCreators.SetShowEditWellbeingServiceModal,
  editWellbeingService: serviceActionCreators.EditWellbeingService,
  setSchema: serviceActionCreators.SetWellbeingServiceSchema,
  updateAssignments: serviceActionCreators.EditWellbeingServiceAssignments,
  setAssignments: serviceActionCreators.SetWellbeingServiceAssignments,
  setWellbeingServiceDraft: serviceActionCreators.SetWellbeingServiceDraft,
  setWellbeingServiceCurrentDetails: serviceActionCreators.SetWellbeingServiceCurrentDetails,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EditWellbeingServicesModal);
